import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { plainToInstance } from "class-transformer";
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { environment } from '../../../../../../environments/environment';
import { MessengerComponent } from '../../../../../chat/components/messenger/messenger.component';
import { AuctionBid } from '../../../../../models/auction-bid.model';
import { Company } from '../../../../../models/company.model';
import { CompanyService } from '../../../../../services/company.service';
import { ComponentCommService } from '../../../../../services/component-comm.service';
import { CurrentDateService } from '../../../../../services/current-date.service';
import { DataDogLoggerService } from '../../../../../services/data-dog-logger.service';
import { Negotiation } from '../../models/negotiation.model';
import { Order } from '../../models/order.model';
import { ODD_BUSINESS_TYPE, SiloBag } from '../../models/silo-bag.model';
import { MemoryStorageService } from '../../services/memory-storage.service';
import { OrderService } from '../../services/order.service';

@Component({
  selector: 'app-view-order',
  templateUrl: './view-order.component.html',
  styleUrls: ['./view-order.component.scss']
})
export class ViewOrderComponent implements OnInit, OnDestroy {

  @ViewChild('bookingModal', { static: true }) private readonly bookingModal: TemplateRef<any>;
  @ViewChild('unavailableModal', { static: true }) private readonly unavailableModal: TemplateRef<any>;
  @ViewChild('enrolledCompanies') private readonly enrolledCompanies: TemplateRef<any>;
  @ViewChild('auctionHistory') private readonly auctionHistoryModal: TemplateRef<any>;
  @ViewChild('messengerComponent') public readonly messengerComponent: MessengerComponent;

  private destroy$ = new Subject<void>();

  public auction_history: AuctionBid[];
  public automatic_offer: number;
  public bid_limit_value: number;
  public canceling_automatic_offer: boolean = false;
  public company: Company;
  public orderId: number;
  /** The language currently used. */
  public currentLang: string;
  public dateEnd: Date;
  public dateFrom: Date;
  public dateExtendTo: Date;
  public enrolling: boolean = false;
  public environment = environment;
  public isAuction: boolean = false;
  public isAuctionEnded: boolean = false;
  public isAuctionLive: boolean = false;
  public isEnrolled: boolean = false;
  public isNegotiationRequestAvailable: boolean = false;
  public isWinning: boolean = false;
  public is_limit_value_valid: boolean = false;
  public offer: any = {};
  public order: Order;
  public placing_bid: boolean = false;
  public processing = false;
  /**
   * Clave de traducción del spinner bloqueante de una acción en curso (ej. "Anotando...").
   * Separado de `processing` porque éste también cubre la carga inicial de la orden.
   */
  public actionMessage: string | null = null;
  public selectedSiloBags: SiloBag[] = [];
  public siloBagCoverageValid: boolean = false;
  /** SiloIDs de un borrador recuperado al volver de "Nueva Contraorden" (ver goToCounterOrder). */
  public draftSiloBagIds: string[] = [];
  public qualityTranslate: any;
  public related_products: Array<any>; // FAS-1862
  public reviewing: boolean = false;

  retiredOrder = new EventEmitter();
  heldOrder = new EventEmitter();
  rejectedOrder = new EventEmitter();

  modalRef: BsModalRef;

  constructor(
    private companyService: CompanyService,
    private componentComm: ComponentCommService,
    private currentDate: CurrentDateService,
    private memoryStorageService: MemoryStorageService,
    private modalService: BsModalService,
    private orderService: OrderService,
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private dataDogLoggerService: DataDogLoggerService
  ) { }

  ngOnInit(): void {
    this.currentLang = this.translateService.currentLang === 'es' ? undefined : this.translateService.currentLang;
    this.orderId = parseInt(this.route.snapshot.paramMap.get('orderId'));

    this.companyService.watch()
      .pipe(takeUntil(this.destroy$))
      .subscribe(company => {
        if (!company) return;

        this.company = company;

        if (this.company.activation_level.id === 1) {
          this.router.navigateByUrl('/companies');
        } else {
          this.isNegotiationRequestAvailable = this.company.market.configuration.order.allow_negotiation_request ? true : false;
          this.loadData();
        }
      });
  }

  private loadData(): void {
    this.processing = true;

    this.orderService.watch(this.company.id, this.orderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(order => {
        if (!order) return;

        this.dataLoaded(order);
        this.processing = false;
      });
  }

  private dataLoaded(order: Order): void {
    // si es una subasta, que ya comenzó, la cual no soy el dueño y no estoy enrolled, no te dejo entrar.
    if (order.type.id === 4 && order.company.id !== this.company.id && !order.auction.company_enrolled) {
      const auction_date_from = new Date(order.auction.date_from);
      if (auction_date_from < this.currentDate.get()) {
        this.router.navigate(['/company/' + this.company.id + '/working-orders']);
        return;
      }
    }

    this.componentComm.emit({ name: 'app-title', title: order.product.name + ' (#' + order.id + ')' });

    this.order = order;
    this.restoreSiloBagsDraft();

    if ([4, 5].indexOf(this.order.type.id) !== -1) {
      // Order is an auction - Pusher WebSocket subscription for real-time auction updates
      this.orderService.watchAuction(this.company.id, order.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(response => {
          if (!response) return;

          this.placing_bid = false;
          this.auction_history = response.bid_list;
          if (this.auction_history.length > 0 && this.auction_history[0].company && this.auction_history[0].company.id === this.company.id) {
            this.isWinning = true;
          } else {
            this.isWinning = false;
          }

          if (response.limit) {
            this.automatic_offer = response.limit.value;
          } else {
            this.automatic_offer = undefined;
          }

          this.bid_limit_value = this.getBidNumber();

          if (response.extend_to) {
            this.dateExtendTo = new Date(response.extend_to);
          }

          this.updateAuctionVariables();
        });

      // Pusher WebSocket subscription for auction start/end moments
      this.orderService.watchAuctionMoments(order.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(response => {
          if (response) {
            if (response.data.event === 'start') {
              this.startAuction();
            } else if (response.data.event === 'end') {
              this.endAuction();
            }
          }
        });

      this.isAuction = true;
      if (this.order.auction.company_enrolled) {
        this.isEnrolled = true;
      }

      this.dateFrom = new Date(this.order.auction.date_from);
      this.dateEnd = new Date(this.order.auction.date_to);

      this.updateAuctionVariables();
    } else {
      this.isAuction = false;
    }

    this.orderService.getQualityTranslate(this.company.id, this.order.product.id, this.order.language)
      .pipe(takeUntil(this.destroy$))
      .subscribe(quality => {
        this.qualityTranslate = this.prepareQualityData(quality);
      });
  }

  private prepareQualityData(quality: any): void {
    let result: any = {};
    for (let i in quality) {
      let attribute = quality[i];
      if (attribute.element != "textarea") {
        if (attribute.empty) {
          result[i] = attribute.empty;
        } else {
          result[i] = attribute.values[0];
        }
      } else {
        result[i] = attribute.value;
      }

      if (attribute.order) {
        result[i].order = attribute.order;
      }
    }
    return result;
  }

  private updateAuctionVariables(): void {
    // Default values
    this.isAuctionLive = false;
    this.isAuctionEnded = false;

    const current = this.currentDate.get(),
      hasStarted = this.dateFrom < current,
      hasEnded = current > (this.dateExtendTo || this.dateEnd), // Check against original end or extended end
      live_period = hasStarted && !hasEnded;

    if (this.order.order_status.group.id === 1) { // Order active
      if (live_period) { // Auction live
        if (this.order.company.id != this.company.id && !this.order.auction.company_enrolled) { // Not enrolled
          this.router.navigate(['/company/' + this.company.id + '/working-orders']);
          return;
        } else { // Owner or enrolled
          this.startAuction();
        }
      } else { // Auction not live yet
      }
    } else { // Order inactive
      if (this.order.order_status.id === 4) { // Order user cancelled
      } else {
        if (hasEnded) { // Auction ended
          this.isAuctionEnded = true;
        }
      }
    }
  }

  public isLimitValueInvalid(): boolean {
    return !this.bid_limit_value || this.placing_bid || this.isAuctionEnded || (this.order.operation_type === 'compra' ? this.bid_limit_value < this.order.auction.step : this.bid_limit_value < this.getBidNumber()) || (this.order.operation_type === 'compra' ? this.bid_limit_value > this.getBidNumber() : false) || !this.isBidStepValid(this.bid_limit_value);
  }

  private isBidStepValid(n: number): boolean {
    let decimalStr = this.order.auction.step.toString().split(".")[1],
      decimalsCount = decimalStr ? decimalStr.length : 0, // Count number of decimals in a step
      decimalRnd = Math.pow(10, decimalsCount),
      remainder = ((n * decimalRnd) % (this.order.auction.step * decimalRnd)); // Check if the bid is multiple

    return remainder === 0;
  }

  private endAuction(): void {
    this.isAuctionLive = false;
    this.isAuctionEnded = true;
    this.router.navigate(['/company', this.company.id, 'my-operations'], {
      queryParams: {
        order_type: 4,
        product_id: this.order.product.id,
        order_by: '-updated_at'
      }
    });
  }

  private startAuction(): void {
    this.isAuctionLive = true;
  }

  public cancelAutomaticAuction(): void {
    if (this.canceling_automatic_offer) return;

    this.canceling_automatic_offer = true;

    this.orderService.cancelAutomaticAuction(this.company.id, this.order.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.canceling_automatic_offer = false;
        },
        error: error => {
          this.canceling_automatic_offer = false;
          this.dataDogLoggerService.warn(error.message, error.error);
        }
      });
  }

  public showEnrolledCompanies(): void {
    this.modalRef = this.modalService.show(this.enrolledCompanies);
  }

  public showAuctionHistory(): void {
    this.modalRef = this.modalService.show(this.auctionHistoryModal);
  }

  public enrollToAuction(): void {
    this.enrolling = true;

    this.orderService.enrollToAuction(this.company.id, this.order)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        if (response.id) {
          this.isEnrolled = true;
        }
      });
  }

  public placeBid(): void {
    this.placing_bid = true;
    this.orderService.placeBid(this.company.id, this.order.id, this.getBidNumber())
      .pipe(takeUntil(this.destroy$))
      .subscribe(resposne => {
      });
  }

  public placeBidValue(): void {
    this.placing_bid = true;
    this.orderService.placeBid(this.company.id, this.order.id, this.getBidNumber(), this.bid_limit_value)
      .pipe(takeUntil(this.destroy$))
      .subscribe(resposne => {
      });
  }

  public getBidNumber(): number {
    if (this.auction_history && this.auction_history.length > 0) {
      if (this.order.operation_type === 'compra') {
        return this.auction_history[0].price.value - this.order.auction.step;
      } else {
        return this.auction_history[0].price.value + this.order.auction.step;
      }
    } else {
      return this.order.business_detail.price.value;
    }
  }

  public hasActiveNegotiations(): boolean {
    for (let index = 0; index < this.order.negotiations.length; index++) {
      // Iterate through visible negotiations
      const negotiation: Negotiation = this.order.negotiations[index];

      if ([1, 2].includes(negotiation.status.group.id) && // Open, Under Negotiation groups
        negotiation.status.id != 7) { // No booked status
        return true;
      }
    }

    return false;
  }

  public negotiationRequest(): void {
    this.processing = true;
    let negotiation: Negotiation = new Negotiation();
    negotiation.order = this.order;
    negotiation.proposal = this.orderService.cloneProposal(this.order);
    negotiation.company = this.company;

    this.orderService.negotiationRequest(negotiation, this.company.id).subscribe(negotiation => {
      this.router.navigate(['/company', this.company.id, 'my-operations'], {
        queryParams: {
          // order_type: 4,
          // product_id: this.order.product.id,
          order_by: '-updated_at'
        }
      });
    });

  }

  public book(quantity: number = 0, from: string = ''): void {
    this.processing = true;
    this.actionMessage = from === 'prebook' ? 'ORDER_VIEW.PROCESSING.PREBOOK' : 'ORDER_VIEW.PROCESSING.BOOK';

    // El modal de cantidad (pre-anotar) quedaría por encima del spinner bloqueante.
    if (this.modalRef) {
      this.modalRef.hide();
      this.modalRef = null;
    }

    // ODD: snapshot de silobolsas comprometidas por el productor al pre-anotar.
    const siloBags = this.isOddOrder ? this.selectedSiloBags.map(b => b.toRef()) : [];

    this.orderService.book(this.order, this.company, quantity, siloBags)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (negotiation: any) => {
          if (from === 'prebook') {
            // El spinner sigue visible hasta que la navegación destruya el componente.
            this.router.navigateByUrl('/company/' + this.company.id + '/counter-order/' + negotiation.id);
          } else {
            this.processing = false;
            this.actionMessage = null;
          }
        },
        error: error => {
          this.handleActionError(error);
        }
      });
  }

  /**
   * True cuando la orden es de tipo de negocio ODD. El `business_type` ya NO se negocia
   * (proposal-detail lo muestra fijo), así que la proposal de la negociación propia es un clon
   * de la orden y ambos coinciden. Se conserva la prioridad proposal→order por los datos
   * anteriores a ese cambio —donde la orden type 7 de un canje puede no conservarlo mientras
   * la proposal sí— y por paridad con counter-order/view-barter y el backend
   * (BartersController::isOddBarterOrder).
   */
  public get isOddOrder(): boolean {
    const type = this.myOddNegotiation?.proposal?.business_detail?.business_type
      ?? this.order?.business_detail?.business_type;
    return type === ODD_BUSINESS_TYPE;
  }

  /** Negociación propia (de esta empresa) más reciente, si existe. */
  public get myOddNegotiation(): Negotiation | undefined {
    if (!this.order?.negotiations?.length || !this.company) return undefined;
    return this.order.negotiations.find(n => n.company?.id === this.company.id);
  }

  /**
   * Negociación con silobolsas ya comprometidas y persistidas. El snapshot lo devuelve el
   * backend para CUALQUIER participante (comprador o vendedor, dueño o contraparte de la
   * negociación), no sólo para la empresa que las comprometió — por eso no se filtra por
   * `company.id` acá.
   */
  public get oddNegotiationWithBags(): Negotiation | undefined {
    if (!this.order?.negotiations?.length) return undefined;
    return this.order.negotiations.find(n => n.silo_bags?.length);
  }

  public get hasCommittedSiloBags(): boolean {
    return !!this.oddNegotiationWithBags?.silo_bags?.length;
  }

  /**
   * Selector editable: la empresa logueada NO es dueña de la orden ODD, la orden sigue
   * publicada, todavía no comprometió silobolsas y no tiene una negociación activa.
   */
  public get showSiloBagSelector(): boolean {
    return this.isOddOrder &&
      this.order?.company?.id !== this.company?.id &&
      this.order?.order_status?.id === 1 &&
      !this.order?.on_hold &&
      !this.hasCommittedSiloBags &&
      !(this.order?.negotiations?.length && this.hasActiveNegotiations());
  }

  public get showCommittedSiloBags(): boolean {
    return this.isOddOrder && this.hasCommittedSiloBags;
  }

  public onSiloBagsChange(bags: SiloBag[]): void {
    this.selectedSiloBags = bags;
  }

  public onSiloBagsValidity(valid: boolean): void {
    this.siloBagCoverageValid = valid;
  }

  /**
   * ODD: guarda la selección de silobolsas en curso antes de ir a "Nueva Contraorden", así
   * `CounterOrderComponent` puede recuperarla (routerLink destruye esta instancia y con ella
   * `selectedSiloBags`, que sólo vive en memoria — ver AGY-3806). Keyed por orderId para no
   * arrastrar el borrador de esta orden a otra distinta.
   */
  public goToCounterOrder(): void {
    if (this.isOddOrder && this.selectedSiloBags.length) {
      this.memoryStorageService.data.siloBagsDraft = {
        orderId: this.order.id,
        siloIds: this.selectedSiloBags.map(bag => bag.silo_id)
      };
    }
    this.router.navigate(['./create-counter-order'], { relativeTo: this.route });
  }

  /** Recupera el borrador guardado por goToCounterOrder(), sólo si es de esta misma orden. */
  private restoreSiloBagsDraft(): void {
    const draft = this.memoryStorageService.data.siloBagsDraft;
    if (draft?.orderId === this.order.id) {
      this.draftSiloBagIds = draft.siloIds ?? [];
    }
    delete this.memoryStorageService.data.siloBagsDraft;
  }

  public prebook(): void {
    if (this.order.business_detail.quantity.type.id === 2) {
      this.offer = {};
      this.modalRef = this.modalService.show(this.bookingModal);
    } else {
      this.book(0, 'prebook');
    }
  }

  public prebookDone(): void {
    this.book(this.offer.quantity, 'prebook');
  }

  public cancelOrder(): void {
    this.processing = true;

    this.orderService.cancel(this.order, this.company.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.orderService.getOrder(this.company.id, this.order.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe(order => {
            this.processing = false;
            this.order = order;
            this.updateAuctionVariables();
            this.retiredOrder.emit();
          });
      });
  }

  public rejectOrder(): void {
    this.orderService.reject(this.order, this.company.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.orderService.getOrder(this.company.id, this.order.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe(order => {
            this.order = order;
            this.rejectedOrder.emit();
          });
      });
  }

  public editOrder(): void {
    this.orderService.onHold(this.order, this.company.id, 1)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.heldOrder.emit();
        if ([4, 5].indexOf(this.order.type.id) !== -1) {
          this.router.navigate(['/company/' + this.company.id + '/edit-auction/' + this.order.id]);
        } else {
          this.router.navigate(['/company/' + this.company.id + '/edit-order/' + this.order.id]);
        }
      });
  }

  public reviewOrder(): void {
    this.reviewing = true;

    this.orderService.validation(Array(this.order), this.company.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        // No pisar otras keys del storage compartido (ej. un siloBagsDraft pendiente de
        // otra orden — ver AGY-3806): mergear en vez de reemplazar `data` entero.
        this.memoryStorageService.data.order = plainToInstance(Order, response.body);
        this.router.navigate(['/company/' + this.company.id + '/create-order']);
      });
    // this.router.navigate(['/company/' + this.company.id + '/create-order-from-preorders/' + this.order.id]);
  }

  public disclose(): void {
    this.processing = true;
    this.orderService.requestDisclosure(this.order, this.company.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: negotiation => {
          this.processing = false;
          this.router.navigate(['/company/' + this.company.id + '/counter-order/' + negotiation.id]);
        },
        error: error => {
          this.handleActionError(error);
        }
      });
  }

  private handleActionError(error: any): void {
    this.dataDogLoggerService.warn(error.message, error.error);
    this.processing = false;
    this.actionMessage = null;
    // TODO: Mostar alerta?

    if (
      error instanceof HttpErrorResponse &&
      error.status === 403
    ) {
      this.modalRef = this.modalService.show(this.unavailableModal, {
        ignoreBackdropClick: true
      });
    } else {
      throw error;
    }
  }

  /** @ignore */
  ngOnDestroy(): void {
    // Complete all subscriptions using takeUntil pattern
    // This includes: companyService.watch, orderService.watch,
    // 2 Pusher WebSocket subscriptions (watchAuction, watchAuctionMoments),
    // and all HTTP subscriptions (auction operations, order actions, etc.)
    this.destroy$.next();
    this.destroy$.complete();
  }
}
